import React, { Component } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import isEqual from 'lodash/isEqual';
import { getMeta } from '../utils';
import { trackShare } from '../actions/trackingActions';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import Transition from 'react-transition-group/Transition';
import ShareButtons from './ShareButtons';


class ShareWidget extends Component {
  constructor (props) {
    super(props);
    this.state = {
      activeTab: 'share',
      shareOptions: [],
      selectedShareOption: null,
      embedSizes: [
        { key: 'small', label: 'Small', width: 640, height: 700 },
        { key: 'medium', label: 'Medium', width: 800, height: 750 },
        { key: 'large', label: 'Large', width: 1024, height: 768 },
        { key: 'custom', label: 'Custom' },
      ],
      customEmbedSize: { width: '', height: '', error: '' },
      selectedEmbedSize: 'small',
      embedCodeCopied: false,
    };

    this.portalEl = document.createElement('div');
    this.portalEl.className = 'share-tools';

    this.handleTabClick = this.handleTabClick.bind(this);
    this.handleShareOptionChange = this.handleShareOptionChange.bind(this);
    this.handleEmbedSizeChange = this.handleEmbedSizeChange.bind(this);
    this.handleEmbedCustomSizeChange = this.handleEmbedCustomSizeChange.bind(this);
    this.handleEmbedCustomSizeFocus = this.handleEmbedCustomSizeFocus.bind(this);
    this.handleButtonClick = this.handleButtonClick.bind(this);
    this.updateShareOptions = this.updateShareOptions.bind(this);
    this.handleEmbedCodeCopy = this.handleEmbedCodeCopy.bind(this);
  }

  componentDidMount () {
    this.updateShareOptions();
    document.body.appendChild(this.portalEl);
  }

  componentDidUpdate (prevProps, prevState) {
    const { metaData } = this.props;
    const { activeTab } = this.state;
    if (activeTab !== prevState.activeTab || !isEqual(metaData, prevProps.metaData)) {
      this.updateShareOptions();
      this.setState({
        customEmbedSize: { width: '', height: '', error: '' },
        selectedEmbedSize: 'small',
        embedCodeCopied: false,
      });
    }
  }

  componentWillUnmount () {
    document.body.removeChild(this.portalEl);
  }

  handleTabClick (e) {
    const { activeTab } = this.state;
    e.preventDefault();
    const tab = e.currentTarget.getAttribute('href').replace('#', '');
    if (tab !== activeTab) {
      this.setState({ activeTab: tab });
    }
  }

  handleShareOptionChange (e) {
    this.setState({ selectedShareOption: e.target.value });
  }

  handleEmbedSizeChange (e) {
    this.setState({ selectedEmbedSize: e.target.value });
    if (e.target.value === 'custom') {
      this.customWidthInput.focus();
    } else {
      this.setState({ customEmbedSize: { width: '', height: '', error: '' } });
    }
  }

  handleEmbedCustomSizeChange (e) {
    const { customEmbedSize } = this.state;
    const dimensionName = e.target.name.replace('embed-custom-', '');
    if (this.validationTimeout) {
      clearTimeout(this.validationTimeout);
    }

    if (/^\d+$/.test(e.target.value)) {
      const val = parseInt(e.target.value, 10);
      let width, height;

      // When the user sets one dimension, automatically calculate
      // the other based on a set aspect ratio.
      if (dimensionName === 'width') {
        width = val;
        // The ideal aspect ratio depends on the breakpoint.
        // Embeds with narrower widths should be more square.
        height = Math.round(val / (val < 1024 ? 1.1 : 1.3));
      } else {
        height = val;
        width = Math.round(val * (val < 930 ? 1.1 : 1.3));
      }
      this.setState({ customEmbedSize: { width, height } });

      // Validate the input after a one-second delay to allow
      // the user to finish typing.
      if (width < 300) {
        this.validationTimeout = setTimeout(() => {
          this.setState({ customEmbedSize: {
            ...this.state.customEmbedSize,
            error: 'Please enter a width of at least 300 pixels.',
          } });
        }, 1000);
      }
    } else if (e.target.value === '') {
      this.setState({ customEmbedSize: { width: '', height: '', error: '' } });
    } else {
      this.setState({ customEmbedSize: {
        ...customEmbedSize,
        [dimensionName]: e.target.value,
        error: 'Please enter a valid number.',
      } });
    }
  }

  handleEmbedCustomSizeFocus () {
    this.setState({ selectedEmbedSize: 'custom' });
  }

  handleEmbedCodeCopy () {
    this.setState({ embedCodeCopied: true }, () => {
      setTimeout(() => {
        this.setState({ embedCodeCopied: false });
      }, 1500);
    });
  }

  handleButtonClick (service) {
    const { actions, metaData, onTriggerShare } = this.props;
    const { selectedShareOption } = this.state;
    const { url } = getMeta(metaData, selectedShareOption);
    onTriggerShare();
    actions.trackShare({ url, sharedVia: service });
  }

  updateShareOptions () {
    const { metaData: { section, site } } = this.props;
    // const { activeTab } = this.state;
    const shareOptions = [
      // ...(activeTab === 'share' ? [{ key: 'page', label: 'Current Page' }] : []),
      { key: 'page', label: 'Current Page' },
      ...(section.url ? [{ key: 'section', label: `Section: ${section.title}` }] : []),
      { key: 'site', label: `Project: ${site.title}` },
    ];
    this.setState({
      shareOptions,
      selectedShareOption: shareOptions[0].key,
    });
  }

  renderEmbedCode () {
    const { metaData } = this.props;
    const { embedSizes, customEmbedSize, selectedEmbedSize, embedCodeCopied, selectedShareOption } = this.state;
    const { url } = getMeta(metaData, selectedShareOption);
    const { width, height, error } = selectedEmbedSize === 'custom' ? customEmbedSize : embedSizes.find(obj => obj.key === selectedEmbedSize);
    const codeString = width && height && !error ? `<iframe src="${url}" width="${width}" height="${height}" frameborder="0"></iframe>` : '';

    return (
      <div>
        <textarea name="embed-code" rows="4" className="d-block mb-2" readOnly value={codeString} />
        <div className="text-right">
          <Transition in={embedCodeCopied} timeout={1000}>
            {state => (
              <span
                className="small meta mr-3"
                style={{
                  transition: state === 'exiting' ? 'opacity 1s ease' : 'none',
                  opacity: state === 'exiting' || state === 'exited' ? 0 : 1,
                }}
              >
                Copied!
              </span>
            )}
          </Transition>
          <CopyToClipboard text={codeString} onCopy={this.handleEmbedCodeCopy}>
            <button>Copy to Clipboard</button>
          </CopyToClipboard>
        </div>
      </div>
    );
  }

  renderPageOptions () {
    const { shareOptions, selectedShareOption } = this.state;
    return (
      <div className="mb-3">
        {shareOptions.map(({ key, label }) => (
          <div key={key} className="share-form-row">
            <input
              type="radio"
              name="share-option"
              id={`option-${key}`}
              value={key}
              onChange={this.handleShareOptionChange}
              checked={selectedShareOption === key}
            />
            <label htmlFor={`option-${key}`}>{label}</label>
          </div>
        ))}
      </div>
    );
  }

  renderShareTab () {
    const { breakpoint, metaData } = this.props;
    const { selectedShareOption } = this.state;
    const { publisher } = metaData;
    const { url, title, description } = getMeta(metaData, selectedShareOption);
    const iconStyles = {
      float: 'left',
      margin: breakpoint.lessThan.md ? '0 3px' : '0 5px',
    };
    const iconSize = 48;
    return (
      <div className="share-shareform">
        {this.renderPageOptions()}
        <div className="d-flex flex-column flex-md-row align-items-md-center">
          {/* TODO: i18n hardcoded string */}
          <h4 className="text-uppercase muted mr-0 mb-2 mr-md-2 mb-md-0">Share On:</h4>
          <div>
            <ShareButtons
              url={url}
              title={title}
              description={description}
              publisher={publisher}
              iconSize={iconSize}
              iconStyles={iconStyles}
              onButtonClick={this.handleButtonClick}
            />
          </div>
        </div>
      </div>
    );
  }

  renderEmbedTab () {
    const { embedSizes, selectedEmbedSize, customEmbedSize } = this.state;
    return (
      <div className="share-embedform">
        {this.renderPageOptions()}
        <h4 className="text-uppercase muted mb-2">Embed Size</h4>
        {embedSizes.map(({ key, label, width, height }) => (
          <div key={key}>
            <div className="share-form-row">
              <input
                type="radio"
                name="embed-size"
                id={`size-${key}`}
                value={key}
                onChange={this.handleEmbedSizeChange}
                checked={selectedEmbedSize === key}
              />
              <label htmlFor={`size-${key}`}>
                {label} {width && height ? <span>({width}x{height})</span> : null}
                {key === 'custom'
                  ? (
                    <span className="ml-2">
                      <input
                        name="embed-custom-width"
                        type="text"
                        size="8"
                        className={customEmbedSize.error ? 'error' : null}
                        value={customEmbedSize.width}
                        onFocus={this.handleEmbedCustomSizeFocus}
                        onChange={this.handleEmbedCustomSizeChange}
                        ref={elem => this.customWidthInput = elem}
                      />
                      <strong className="mx-1 muted" style={{ fontWeight: 700 }}>&times;</strong>
                      <input
                        name="embed-custom-height"
                        type="text"
                        size="8"
                        className={customEmbedSize.error ? 'error' : null}
                        value={customEmbedSize.height}
                        onFocus={this.handleEmbedCustomSizeFocus}
                        onChange={this.handleEmbedCustomSizeChange}
                      />
                    </span>
                  ) : null
                }
              </label>
            </div>
            {key === 'custom' && customEmbedSize.error ? <div className="share-form-error">{customEmbedSize.error}</div> : null}
          </div>
        ))}
        <div className="share-form-row mt-3">
          {this.renderEmbedCode()}
        </div>
      </div>
    );
  }

  render () {
    const { transitionState } = this.props;
    // const { activeTab } = this.state;
    const transitionStyles = {
      entering: { opacity: 1 },
      exiting: { opacity: 0 },
      exited: { opacity: 0, visibility: 'hidden' },
    };

    return createPortal(
      <div className="share-wrapper" style={{ ...transitionStyles[transitionState] }}>
        {this.renderShareTab()}
      </div>,
      this.portalEl,
    );

    // NOTE: Embed option disabled

    // return (
    //   <div className="share-wrapper" style={{ ...transitionStyles[transitionState] }}>
    //     <header>
    //       <a href="#share" className={activeTab === 'share' ? 'active' : null} onClick={this.handleTabClick}>
    //         <h3>Share</h3>
    //       </a>
    //       <a href="#embed" className={activeTab === 'embed' ? 'active' : null} onClick={this.handleTabClick}>
    //         <h3>Embed</h3>
    //       </a>
    //     </header>
    //     {activeTab === 'embed' ? this.renderEmbedTab() : this.renderShareTab()}
    //   </div>
    // );
  }
}

ShareWidget.propTypes = {
  metaData: PropTypes.object,
  onTriggerShare: PropTypes.func,
  transitionState: PropTypes.string,
};

ShareWidget.defaultProps = {
  metaData: {},
  onTriggerShare: () => {},
};

const mapStateToProps = ({ metaData, breakpoint }) => ({ metaData, breakpoint });

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({ trackShare }, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(ShareWidget);



// WEBPACK FOOTER //
// ./src/components/ShareWidget.js